import { signInWithCode, sendCustomChallengeAnswer, resendCode } from '@lastbrand/fe-common/universal-auth'
import { useSearchParams } from 'next/navigation'
import { useTranslation } from 'next-i18next'
import React, { SyntheticEvent, useState } from 'react'

import { STATSIG_EXPERIMENTS, STATSIG_EXPERIMENTS_BUCKETS } from '@src/config/statsig-experiments.config'
import { useStatsigExperiment } from '@src/experiments/statsig-integration'
import { Button } from '@src/quince-components/src'
import { BUTTON_SIZE, BUTTON_TYPES } from '@src/quince-components/src/atoms/button/button.constants'
import { B2B_SOURCE } from '@src/quince-site-common/src/services/cartCheckoutService/constants'
import { getlbUserId } from '@src/quince-site-common/src/utils/getlb-user-id'
import { getSanitizedHtml } from '@src/quince-site-common/src/utils/getSanitizedHtml'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'
import { getStandardLocaleFromCookie } from '@src/utils/i18n.utils'

import FormFooter from '../../../../../components/Auth/FormFooter'
import FormHeader from '../../../../../components/Auth/FormHeader'
import { BoxStyledErrorMessage } from '../../../../../components/FormElements'
import { StyledVerificationMessage } from '../../../../../components/FormElements/StyledVerificationMessage'
import { ContinueButton } from '../../../../LogIn/Components/ContinueButton'
import { Email } from '../../../../LogIn/Components/Email'
import styles from '../../../../LogIn/Components/loginComponents.module.scss'
import { Password } from '../../../../LogIn/Components/Password'
import useForm from '../../../../LogIn/hook/useForm'

import SignInCodeModal from './SignInCodeModal'

interface SecondaryPagePropTypes {
  serverError?: { message: string }
  unverifiedAccount?: boolean
  resendVerificationEmail: (email?: string) => void
  verificationEmailSent?: boolean
  email?: string
  backToLogin: () => void
  loading?: boolean
  login: (data: object) => void
  onEmailEdit: () => void
  isInResetPasswordFlow?: boolean
  error?: string | null
}

// @i18n-ignore
const RESEND_BUTTON_TRACK_CATEGORY = 'login'
// @i18n-ignore
const LOGIN_FORM_A_SECONDARY = 'login-form-A-secondary'

const SecondaryPage = ({
  serverError,
  unverifiedAccount,
  resendVerificationEmail,
  verificationEmailSent,
  email,
  backToLogin,
  loading,
  login,
  onEmailEdit,
  isInResetPasswordFlow,
  error,
}: SecondaryPagePropTypes) => {
  const { t } = useTranslation('common')
  const { t: tAccountPage } = useTranslation('accountPage')
  const searchParams = useSearchParams()
  const [errors, setError] = useState({})
  const { email: userEmail, password, onPasswordChange, onEmailChange } = useForm({ email })

  // Sign-in code modal state
  const [isSignInCodeModalOpen, setIsSignInCodeModalOpen] = useState(false)
  const [signInCodeError, setSignInCodeError] = useState<string | null>(null)
  const [signInCodeLoading, setSignInCodeLoading] = useState(false)
  const [resendCodeLoading, setResendCodeLoading] = useState(false)
  const { experimentBucket, init } = useStatsigExperiment(STATSIG_EXPERIMENTS.SIGN_IN_WITH_CODE)
  const isSignInWithCodeEnabled = experimentBucket === STATSIG_EXPERIMENTS_BUCKETS.SHOW && init

  const standardLocale = getStandardLocaleFromCookie()
  const shouldIncludeLocale = typeof window !== 'undefined' && window.__debug?.isI18nEnabled && standardLocale

  const onFormSubmit = (e: SyntheticEvent) => {
    e.preventDefault()

    if (!password) {
      setError({
        password: {
          message: t('please_fill_in_password'),
        },
      })
    } else {
      login({
        password,
        username: isInResetPasswordFlow ? userEmail : email,
      })
    }
  }

  const handleSignInWithCode = async () => {
    if (!email) return

    setSignInCodeLoading(true)
    setSignInCodeError(null)

    try {
      const { needCustomChallenge } = await signInWithCode(email, {
        ...(shouldIncludeLocale
          ? {
              locale: standardLocale,
            }
          : {}),
      })
      if (needCustomChallenge) {
        setIsSignInCodeModalOpen(true)
      } else {
        setSignInCodeError(t('failed_to_send_code'))
      }
    } catch (error: any) {
      const errorCode = error?.code

      if (errorCode === 'FEDERATED_USER_NOT_SUPPORTED') {
        setSignInCodeError(t('federated_user_not_supported'))
      } else if (errorCode === 'USER_NOT_FOUND') {
        setSignInCodeError(t('user_not_found'))
      } else if (errorCode === 'CUSTOM_AUTH_NOT_AVAILABLE') {
        setSignInCodeError(t('custom_auth_not_available'))
      } else if (errorCode === 'LimitExceededException' || error?.message?.includes('LimitExceededException')) {
        setSignInCodeError(tAccountPage('verification_attempts_limit'))
      } else {
        setSignInCodeError(t('failed_to_send_code'))
      }
    } finally {
      setSignInCodeLoading(false)
    }
  }

  const handlesendCustomChallengeAnswer = async (otpCode: string) => {
    if (!otpCode) return

    setSignInCodeLoading(true)
    setSignInCodeError(null)

    try {
      const result = await sendCustomChallengeAnswer(otpCode)

      if (result.isSignedIn) {
        setIsSignInCodeModalOpen(false)
        trackEvent(TELEMETRY_EVENT_NAMES.OTP_SIGN_IN_SUCCESS, { email: email ?? '' })
        // Trigger the login success flow
        login({
          username: email,
        })
      } else {
        setSignInCodeError(tAccountPage('incorrect_verification_code'))
        trackEvent(TELEMETRY_EVENT_NAMES.OTP_SIGN_IN_FAILURE, {
          email: email ?? '',
          error: 'incorrect_verification_code',
        })
      }
    } catch (error: any) {
      if (error?.code === 'CodeMismatchException' || error?.message?.includes('CodeMismatchException')) {
        setSignInCodeError(tAccountPage('incorrect_verification_code'))
      } else if (error?.code === 'ExpiredCodeException') {
        setSignInCodeError(t('verification_code_expired'))
      } else {
        setSignInCodeError(t('verification_code_failed'))
      }
      trackEvent(TELEMETRY_EVENT_NAMES.OTP_SIGN_IN_FAILURE, {
        email: email ?? '',
        error: error?.code || error?.message || 'unknown_error',
      })
    } finally {
      setSignInCodeLoading(false)
    }
  }

  const handleResendCode = async () => {
    if (!email) return

    setResendCodeLoading(true)
    setSignInCodeError(null)

    try {
      await resendCode(email, {
        ...(shouldIncludeLocale
          ? {
              locale: standardLocale,
            }
          : {}),
      })
      trackEvent(TELEMETRY_EVENT_NAMES.OTP_SIGN_IN_RESEND, { email })
    } catch (error: any) {
      if (error?.code === 'LimitExceededException' || error?.message?.includes('LimitExceededException')) {
        setSignInCodeError(tAccountPage('verification_attempts_limit'))
      } else {
        setSignInCodeError(t('verification_resend_failed'))
      }
    } finally {
      setResendCodeLoading(false)
    }
  }

  return (
    <>
      <form id="login-form-A-secondary" onSubmit={onFormSubmit} noValidate>
        <FormHeader>
          <div className={styles.formHeading}>{t('welcome_back')}</div>
          <div className={styles.formSubHeading}>
            {t(
              searchParams?.get('source') === B2B_SOURCE
                ? 'b2b_sign_in_register_message'
                : 'free_shipping_365_day_ret_atbvw9',
            )}
          </div>
        </FormHeader>
        {serverError && (
          <BoxStyledErrorMessage dangerouslySetInnerHTML={{ __html: getSanitizedHtml(serverError.message) }} />
        )}
        {error && <BoxStyledErrorMessage>{error}</BoxStyledErrorMessage>}
        {signInCodeError && <BoxStyledErrorMessage>{signInCodeError}</BoxStyledErrorMessage>}
        {unverifiedAccount && (
          <StyledVerificationMessage>
            {t('cannot_find_verification_mail')}
            <Button
              onClick={() => {
                resendVerificationEmail(email)
              }}
              trackParams={{
                trackInternal: true,
                category: RESEND_BUTTON_TRACK_CATEGORY,
                action: 'account_login_unverified',
                context: {
                  login_email: email ?? '',
                  lb_user_id: getlbUserId(),
                },
              }}
              className={styles.unVerifiedMessage}
            >
              {t('resend_one')}
            </Button>
          </StyledVerificationMessage>
        )}
        {verificationEmailSent && (
          <StyledVerificationMessage>{t('verification_e_mail_sent')}</StyledVerificationMessage>
        )}

        <Email
          errors={errors}
          disabled={!isInResetPasswordFlow}
          value={userEmail}
          onChange={isInResetPasswordFlow ? onEmailChange : undefined}
          onEdit={onEmailEdit}
          showEditIcon={!isInResetPasswordFlow}
        />
        <Password
          errors={errors}
          email={email}
          backToLogin={backToLogin}
          value={password}
          onChange={onPasswordChange}
          isInResetPasswordFlow={isInResetPasswordFlow}
        />
        <FormFooter>
          <ContinueButton
            loading={loading}
            form={LOGIN_FORM_A_SECONDARY}
            className={isSignInWithCodeEnabled ? styles.continueButtonWithOTP : undefined}
          />
        </FormFooter>

        {isSignInWithCodeEnabled && (
          <>
            {/** OR seperator */}

            <div className={styles.orSeparator}>
              <div className={styles.orSeparatorLine} />
              <div className={styles.orSeparatorText}>{t('or')}</div>
              <div className={styles.orSeparatorLine} />
            </div>

            <FormFooter>
              <Button
                size={BUTTON_SIZE.MEDIUM}
                type={BUTTON_TYPES.OUTLINED}
                className={styles.signInWithCodeButton}
                onClick={() => {
                  void handleSignInWithCode()
                }}
                disabled={signInCodeLoading}
                showLoader={signInCodeLoading}
                id="sign-in-with-code-button"
              >
                {t('sign_in_with_code')}
              </Button>
            </FormFooter>
          </>
        )}
      </form>

      <SignInCodeModal
        open={isSignInCodeModalOpen}
        onClose={() => {
          setIsSignInCodeModalOpen(false)
          setSignInCodeError(null)
        }}
        email={email ?? ''}
        onSubmit={handlesendCustomChallengeAnswer}
        onResend={() => {
          void handleResendCode()
        }}
        error={signInCodeError}
        loading={signInCodeLoading}
        resendLoading={resendCodeLoading}
      />
    </>
  )
}

export { SecondaryPage }
